import { z } from 'zod';
import { vinSchema } from './primitives';

/**
 * The subsystem a modification or wishlist item applies to.
 *
 * Mirrors `ModificationCategory` on the server. The generated API client carries the same union,
 * but forms need a runtime value to build a picker from, which a type alone cannot provide.
 */
export const modificationCategories = [
  'Engine',
  'Drivetrain',
  'Suspension',
  'Wheels',
  'Brakes',
  'Exterior',
  'Interior',
  'Electronics',
  'Other',
] as const;

/** A modification category. */
export type ModificationCategory = (typeof modificationCategories)[number];

/** Category values as a Zod enum. */
export const modificationCategorySchema = z.enum(modificationCategories);

/**
 * Earliest model year accepted, matching the server. The Benz Patent-Motorwagen was 1886.
 */
export const MIN_MODEL_YEAR = 1886;

/** Latest model year accepted, matching the server. */
export const MAX_MODEL_YEAR = 2100;

/**
 * An optional free-text field: blank input becomes `undefined` rather than an empty string.
 *
 * Users leave fields empty constantly, and sending `""` would store a value that reads as "the
 * owner deliberately entered nothing". Absent and empty are different facts.
 */
function optionalText(max: number) {
  return z
    .string()
    .trim()
    .max(max)
    .transform((value) => (value === '' ? undefined : value))
    .optional();
}

/** Validates the vehicle create and edit form. */
export const vehicleFormSchema = z.object({
  year: z
    .number({ error: 'Enter a model year.' })
    .int('Enter a whole year.')
    .min(MIN_MODEL_YEAR, `Year must be ${MIN_MODEL_YEAR} or later.`)
    .max(MAX_MODEL_YEAR, `Year must be ${MAX_MODEL_YEAR} or earlier.`),
  make: z.string().trim().min(1, 'Enter the make.').max(100),
  model: z.string().trim().min(1, 'Enter the model.').max(100),
  trim: optionalText(100),
  // A VIN is optional, but if one is given it must survive the check digit.
  vin: z.union([z.literal(''), vinSchema]).optional(),
  nickname: optionalText(100),
  isPublic: z.boolean(),
});

/** The validated vehicle form value. */
export type VehicleFormValues = z.infer<typeof vehicleFormSchema>;

/** Validates the modification create and edit form. */
export const modificationFormSchema = z.object({
  category: modificationCategorySchema,
  title: z.string().trim().min(1, 'Enter a title.').max(200),
  notes: optionalText(4000),
  cost: z.number().min(0, 'Cost cannot be negative.').optional(),
  installedDate: z.iso.date().optional(),
  installer: optionalText(200),
});

/** The validated modification form value. */
export type ModificationFormValues = z.infer<typeof modificationFormSchema>;

/** Validates the wishlist create and edit form. */
export const wishlistFormSchema = z.object({
  category: modificationCategorySchema,
  title: z.string().trim().min(1, 'Enter a title.').max(200),
  estimatedCost: z.number().min(0, 'Cost cannot be negative.').optional(),
  url: z.union([z.literal(''), z.url('Enter a full URL, including https://')]).optional(),
  notes: optionalText(4000),
});

/** The validated wishlist form value. */
export type WishlistFormValues = z.infer<typeof wishlistFormSchema>;
